// packages/web/src/pages/ResetPasswordPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { AuthService } from '@matchpoint/shared';

// Styled components matching landing page
const PageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  backgroundColor: '#fafafa',
});

const ResetPasswordContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  padding: theme.spacing(3),
}));

const ResetPasswordPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: 480,
  width: '100%',
  borderRadius: '16px',
  border: '1px solid #e5e5e5',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  backgroundColor: '#ffffff',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(4),
  gap: theme.spacing(1.5),
}));

const LogoImage = styled('div')(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #84b817 0%, #c4d82f 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(132, 184, 23, 0.3)',
  '&::before': {
    content: '""',
    position: 'absolute',
    width: 18,
    height: 18,
    borderRadius: '50%',
    backgroundColor: '#c4d82f',
    top: 8,
    left: 8,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    width: 14,
    height: 14,
    borderRadius: '50%',
    backgroundColor: '#679614',
    bottom: 8,
    right: 8,
  },
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontSize: '1.75rem',
  fontWeight: 700,
  color: '#1a1a1a',
  fontFamily: '"Helvetica Neue", Arial, sans-serif',
  letterSpacing: '-0.03em',
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  marginBottom: theme.spacing(1),
  color: '#1a1a1a',
  letterSpacing: '-0.02em',
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.75rem',
  },
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#666',
  marginBottom: theme.spacing(4),
  textAlign: 'center',
  fontWeight: 400,
  lineHeight: 1.6,
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#c4d600',
  color: '#1a1a1a',
  fontSize: '1rem',
  fontWeight: 700,
  padding: theme.spacing(1.5, 3),
  borderRadius: '8px',
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(196, 214, 0, 0.25)',
  border: 'none',
  '&:hover': {
    backgroundColor: '#b3c200',
    boxShadow: '0 6px 16px rgba(196, 214, 0, 0.35)',
    transform: 'translateY(-1px)',
  },
  '&:disabled': {
    backgroundColor: '#e5e5e5',
    color: '#999',
  },
  transition: 'all 0.2s ease',
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: '#1a1a1a',
  fontSize: '1rem',
  fontWeight: 600,
  padding: theme.spacing(1.5, 3),
  borderRadius: '8px',
  border: '2px solid #e5e5e5',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    borderColor: '#1a1a1a',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#fafafa',
    '& fieldset': {
      borderColor: '#e5e5e5',
    },
    '&:hover fieldset': {
      borderColor: '#c4d600',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#c4d600',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#666',
    '&.Mui-focused': {
      color: '#1a1a1a',
    },
  },
}));

export const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const [checking, setChecking] = useState(true);
  const isMountedRef = useRef(true);

  const authService = AuthService.getInstance();

  useEffect(() => {
    const resetToken = searchParams.get('token');
    if (!resetToken) {
      setError('Invalid reset link');
      setChecking(false);
      return;
    }

    setToken(resetToken);
    verifyToken(resetToken);
  }, [searchParams]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const verifyToken = async (resetToken: string) => {
    try {
      const result = await authService.verifyPasswordResetToken(resetToken);
      if (!isMountedRef.current) return;
      
      if (result.valid) {
        setTokenValid(true);
        setEmail(result.email || '');
      } else {
        setError('Invalid or expired reset link');
      }
    } catch (error: any) {
      if (!isMountedRef.current) return;
      setError(error.message);
    } finally {
      if (!isMountedRef.current) return;
      setChecking(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!token) {
      setError('Invalid reset token');
      return;
    }

    setLoading(true);

    try {
      await authService.completePasswordReset(token, newPassword);
      if (!isMountedRef.current) return;
      navigate('/login?message=password-reset-success');
    } catch (error: any) {
      if (!isMountedRef.current) return;
      setError(error.message);
    } finally {
      if (!isMountedRef.current) return;
      setLoading(false);
    }
  };

  // Loading state
  if (checking) {
    return (
      <PageContainer>
        <ResetPasswordContainer>
          <ResetPasswordPaper>
            <LogoContainer>
              <LogoImage />
              <LogoText>sched tennis</LogoText>
            </LogoContainer>
            <CircularProgress sx={{ mb: 3, color: '#c4d600' }} />
            <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center' }}>
              Verifying reset link...
            </Typography>
          </ResetPasswordPaper>
        </ResetPasswordContainer>
      </PageContainer>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <PageContainer>
        <ResetPasswordContainer>
          <ResetPasswordPaper>
            <LogoContainer>
              <LogoImage />
              <LogoText>sched tennis</LogoText>
            </LogoContainer>
            
            <PageTitle sx={{ color: '#e74c3c' }}>
              Invalid Reset Link
            </PageTitle>
            
            <Alert severity="error" sx={{ width: '100%', mb: 4, borderRadius: '8px' }}>
              {error}
            </Alert>
            
            <PrimaryButton
              href="/forgot-password"
              fullWidth
              sx={{ mb: 2 }}
            >
              Request New Reset Link
            </PrimaryButton>
            
            <SecondaryButton
              href="/login"
              fullWidth
            >
              Back to Login
            </SecondaryButton>
          </ResetPasswordPaper>
        </ResetPasswordContainer>
      </PageContainer>
    );
  }

  // Valid token - password reset form
  return (
    <PageContainer>
      <ResetPasswordContainer>
        <ResetPasswordPaper>
          <LogoContainer>
            <LogoImage />
            <LogoText>sched tennis</LogoText>
          </LogoContainer>

          <PageTitle>Set New Password</PageTitle>
          <PageSubtitle>
            Enter your new password below
          </PageSubtitle>
          
          {email && (
            <Alert severity="info" sx={{ width: '100%', mb: 3, borderRadius: '8px' }}>
              Resetting password for: <strong>{email}</strong>
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 3, borderRadius: '8px' }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <StyledTextField
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              required
              disabled={loading}
              inputProps={{ minLength: 8 }}
              helperText="Must be at least 8 characters long"
            />

            <StyledTextField
              fullWidth
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
              disabled={loading}
            />

            <PrimaryButton
              type="submit"
              fullWidth
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? 'Updating Password...' : 'Update Password'}
            </PrimaryButton>

            <SecondaryButton
              fullWidth
              href="/login"
            >
              Back to Login
            </SecondaryButton>
          </Box>
        </ResetPasswordPaper>
      </ResetPasswordContainer>
    </PageContainer>
  );
};